import React from "react";
import type { Excursion } from "api/excursions/Excursion.typed";

export const PLANNING_GROUPS_DRAWER_OPEN_DELAY_MS = 200;

export function usePlanningGroupsDrawerOpenSchedule() {
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  const cancelScheduledDrawer = React.useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleOpenDrawer = React.useCallback(
    (openDrawer: (excursionId: number) => void, excursionId: number) => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        openDrawer(excursionId);
      }, PLANNING_GROUPS_DRAWER_OPEN_DELAY_MS);
    },
    [],
  );

  return { scheduleOpenDrawer, cancelScheduledDrawer };
}

export type PlanningExcursionCardClickOptions = {
  canOpenExcursionDrawer: boolean;
  canEdit: boolean;
  onOpenExcursionFullModal?: (e: React.MouseEvent, excursion: Excursion) => void;
  openPlanningGroupsDrawer: (excursionId: number) => void;
  scheduleOpenDrawer: (openDrawer: (excursionId: number) => void, excursionId: number) => void;
  cancelScheduledDrawer: () => void;
  excursion: Excursion;
};

/** Single click opens the groups drawer after a delay; second click of a double opens the full edit modal. */
export function createPlanningExcursionCardClickHandler(
  opts: PlanningExcursionCardClickOptions,
): ((ev: React.MouseEvent) => void) | undefined {
  const {
    canOpenExcursionDrawer,
    canEdit,
    onOpenExcursionFullModal,
    openPlanningGroupsDrawer,
    scheduleOpenDrawer,
    cancelScheduledDrawer,
    excursion: ex,
  } = opts;
  if (!canOpenExcursionDrawer && !(canEdit && onOpenExcursionFullModal)) return undefined;
  return (ev: React.MouseEvent) => {
    ev.stopPropagation();
    if (canEdit && onOpenExcursionFullModal && ev.detail === 2) {
      cancelScheduledDrawer();
      onOpenExcursionFullModal(ev, ex);
      return;
    }
    if (canOpenExcursionDrawer) {
      scheduleOpenDrawer(openPlanningGroupsDrawer, ex.id);
    }
  };
}
